import React, { useState, useEffect } from 'react';
import {
    Card, Form, Select, Input, Button, Table, Row, Col, Typography,
    DatePicker, Space, Badge, message, Tooltip, InputNumber
} from 'antd';
import {
    SearchOutlined, ClearOutlined, FileExcelOutlined, FilePdfOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { orderService } from '../services/orderService';
import { channelPartnerService } from '../services/channelPartnerService';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MILESTONE_GROUPS = [
    {
        label: 'Milestone 1: Preparation',
        options: [
            { value: 'docsReceivedForNP', label: 'Docs Received (NP)' },
            { value: 'docsReceivedForJanSamarth', label: 'Docs Received (JanSamarth)' },
            { value: 'registrationOnNPDone', label: 'Registration Done (NP)' },
            { value: 'emailIdChangeOnMSEB', label: 'Email ID Updated on MSEB' },
            { value: 'registrationOnJanSamarthDone', label: 'Registration Done (JanSamarth)' },
        ]
    },
    {
        label: 'Milestone 2: Approvals & Bank',
        options: [
            { value: 'sanctionLetterReceived', label: 'Sanction Letter Received' },
            { value: 'docsSentToBank', label: 'Docs Sent to Bank' },
            { value: 'loanAmountReceived', label: 'Loan Amount Received / Customer Advance' },
        ]
    },
    {
        label: 'Milestone 3: Installation',
        options: [
            { value: 'structureOrdered', label: 'Structure Ordered' },
            { value: 'materialDispatched', label: 'Material Dispatched' },
            { value: 'geoTaggedPhoto', label: 'GeoTagged Photo Uploaded' },
            { value: 'installationCompleted', label: 'Installation Completed' },
        ]
    },
    {
        label: 'Milestone 4: Grid Connection',
        options: [
            { value: 'genMeterTestReportLetter', label: 'Gen Meter Test Report Letter' },
            { value: 'genMeterTested', label: 'Gen Meter Tested' },
            { value: 'msebDocsSubmitted', label: 'MSEB Docs Submitted' },
            { value: 'releaseNoteReceived', label: 'Release Note Received' },
            { value: 'metersConnected', label: 'Meters Connected' },
            { value: 'metersPunched', label: 'Meters Punched & Checked' },
        ]
    },
    {
        label: 'Milestone 5: Close Out',
        options: [
            { value: 'bankStatementObtained', label: 'Bank Statement Obtained' },
            { value: 'dcrCertificateReady', label: 'DCR Certificate Ready' },
            { value: 'installationDetailsFilledOnNP', label: 'Install Details on NP' },
            { value: 'inspectionApprovedByMSEB', label: 'Inspection Approved (MSEB)' },
            { value: 'appliedForSubsidy', label: 'Applied for Subsidy' },
        ]
    },
    {
        label: 'Milestone 6: Post Installation',
        options: [
            { value: 'panelWarranty', label: 'Warranty for Panels' },
            { value: 'inverterWarranty', label: 'Warranty for Inverters' }
        ]
    }
];

const OrderReports = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [channelPartners, setChannelPartners] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    useEffect(() => {
        fetchChannelPartners();
        fetchOrders(1, 15);
    }, []);

    const fetchChannelPartners = async () => {
        try {
            const res = await channelPartnerService.getAllChannelPartners({ limit: 1000 });
            setChannelPartners(res.data || []);
        } catch (error) {
            console.error('Failed to fetch channel partners', error);
        }
    };

    const fetchOrders = async (page = 1, pageSize = 15, currentFilters = null) => {
        setLoading(true);
        try {
            const appliedFilters = currentFilters || buildFilters(form.getFieldsValue());
            const params = {
                page,
                limit: pageSize,
                ...appliedFilters
            };
            const response = await orderService.getAllOrders(params);

            setOrders(response.data || []);
            setPagination({
                current: response.pagination.page,
                pageSize: response.pagination.limit,
                total: response.pagination.total
            });
        } catch (error) {
            message.error('Failed to fetch orders.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (values) => {
        fetchOrders(1, pagination.pageSize, buildFilters(values));
    };

    const handleReset = () => {
        form.resetFields();
        fetchOrders(1, pagination.pageSize, {});
    };

    const handleTableChange = (pag) => {
        fetchOrders(pag.current, pag.pageSize);
    };

    const buildFilters = (values) => {
        const filters = {};
        if (values.dateRange?.length === 2) {
            filters.startDate = values.dateRange[0].startOf('day').toISOString();
            filters.endDate = values.dateRange[1].endOf('day').toISOString();
        }
        if (values.district) filters.district = values.district;
        if (values.plantSizeKw) filters.kw = values.plantSizeKw;
        if (values.bankLoanYN) filters.bankLoanYN = values.bankLoanYN;
        if (values.status) filters.status = values.status;
        if (values.channelPartnerId) filters.channelPartnerId = values.channelPartnerId;
        if (values.milestones?.length > 0) {
            filters.milestones = values.milestones.join(',');
        }
        if (values.sortBy) filters.sortBy = values.sortBy;
        if (values.sortOrder) filters.sortOrder = values.sortOrder;
        return filters;
    };

    // --- EXPORT ---
    const fetchAllForExport = async () => {
        setLoading(true);
        try {
            const filters = buildFilters(form.getFieldsValue());
            const params = { page: 1, limit: 5000, ...filters };
            const response = await orderService.getAllOrders(params);
            return response.data || [];
        } catch (error) {
            message.error('Export failed.');
            return [];
        } finally {
            setLoading(false);
        }
    };

    const formatExportData = (data) => {
        return data.map(order => {
            const completedStages = MILESTONE_GROUPS
                .filter(group => group.options.every(opt => order[opt.value] === true))
                .map(group => group.label.split(':')[0].trim())
                .join(', ');

            return {
                'Order #': order.id,
                'Date': dayjs(order.orderDate).format('YYYY-MM-DD'),
                'Customer': order.customer?.customerName || '-',
                'Mobile': order.customer?.customerMobile || '-',
                'District': order.customer?.district || '-',
                'Size (kW)': order.plantSizeKw || '-',
                'Value': order.orderValue || 0,
                'Status': order.projectStatus,
                'Bank Loan': order.bankLoanYN === 'Y' ? 'Yes' : 'No',
                'Milestone completed': completedStages || 'None'
            };
        });
    };

    const handleExportExcel = async () => {
        const data = await fetchAllForExport();
        if (!data.length) return message.warning('No data to export');
        const worksheet = XLSX.utils.json_to_sheet(formatExportData(data));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
        XLSX.writeFile(workbook, `Order_Report_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    const handleExportPDF = async () => {
        const data = await fetchAllForExport();
        if (!data.length) return message.warning('No data to export');
        const doc = new jsPDF('landscape');
        doc.setFontSize(14).text('Order Report', 14, 15);
        doc.setFontSize(9).text(`Generated: ${dayjs().format('DD-MM-YYYY HH:mm')}`, 14, 22);
        const formatted = formatExportData(data);
        autoTable(doc, {
            startY: 28,
            head: [Object.keys(formatted[0])],
            body: formatted.map(item => Object.values(item)),
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [80, 80, 80] }
        });
        doc.save(`Order_Report_${dayjs().format('YYYYMMDD')}.pdf`);
    };

    const columns = [
        { title: '#', dataIndex: 'id', key: 'id', width: 60 },
        {
            title: 'Date',
            dataIndex: 'orderDate',
            key: 'orderDate',
            width: 110,
            render: (d) => dayjs(d).format('DD-MM-YYYY')
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (r) => (
                <div>
                    <Text strong>{r.customer?.customerName}</Text><br />
                    <Text type="secondary" size="small">{r.customer?.customerMobile}</Text>
                </div>
            )
        },
        { title: 'District', key: 'dist', render: (r) => r.customer?.district || '-' },
        { title: 'Channel Partner', key: 'cp', render: (r) => r.channelPartner?.partnerName || '-' },
        { title: 'Size', dataIndex: 'plantSizeKw', key: 'kw', render: (v) => v ? `${v} kW` : '-' },
        { title: 'Value', dataIndex: 'orderValue', key: 'val', render: (v) => `₹${v?.toLocaleString()}` },
        {
            title: 'Status',
            dataIndex: 'projectStatus',
            key: 'status',
            render: (s) => <Badge status={s === 'Completed' ? 'success' : s === 'InProgress' ? 'processing' : 'default'} text={s} />
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col><Title level={4} style={{ margin: 0 }}>Order Reports</Title></Col>
                <Col>
                    <Space>
                        <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>Export to Excel</Button>
                        <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>Export to PDF</Button>
                    </Space>
                </Col>
            </Row>

            <Card size="small" style={{ marginBottom: 16 }}>
                <Form form={form} layout="vertical" onFinish={handleSearch}>
                    <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="dateRange" label="Date Range" style={{ marginBottom: 8 }}>
                                <RangePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="district" label="District" style={{ marginBottom: 8 }}>
                                <Input placeholder="All Districts" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                            <Form.Item name="plantSizeKw" label="Plant Size (kW)" style={{ marginBottom: 8 }}>
                                <InputNumber style={{ width: '100%', borderRadius: 6 }} placeholder="Any" min={0} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                            <Form.Item name="bankLoanYN" label="Bank Loan" style={{ marginBottom: 8 }}>
                                <Select placeholder="Any" allowClear>
                                    <Option value="Y">Yes</Option>
                                    <Option value="N">No</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                            <Form.Item name="status" label="Status" style={{ marginBottom: 8 }}>
                                <Select placeholder="Any" allowClear>
                                    <Option value="Open">Open</Option>
                                    <Option value="InProgress">In Progress</Option>
                                    <Option value="Completed">Completed</Option>
                                    <Option value="Closed">Closed</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="channelPartnerId" label="Channel Partner" style={{ marginBottom: 8 }}>
                                <Select
                                    showSearch placeholder="All Partners"
                                    optionFilterProp="children" allowClear
                                >
                                    {channelPartners.map(cp => <Option key={cp.id} value={cp.id}>{cp.partnerName}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="milestones" label="Milestones Completed" style={{ marginBottom: 8 }}>
                                <Select mode="multiple" placeholder="Select Milestones" maxTagCount="responsive" allowClear>
                                    {MILESTONE_GROUPS.map(g => (
                                        <Select.OptGroup key={g.label} label={g.label}>
                                            {g.options.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                                        </Select.OptGroup>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                            <Form.Item name="sortBy" label="Sort By" initialValue="createdAt" style={{ marginBottom: 8 }}>
                                <Select>
                                    <Option value="id">Order #</Option>
                                    <Option value="orderDate">Order Date</Option>
                                    <Option value="createdAt">Created Date</Option>
                                    <Option value="orderValue">Order Value</Option>
                                    <Option value="plantSizeKw">Plant Size (kW)</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                            <Form.Item name="sortOrder" label="Order" initialValue="desc" style={{ marginBottom: 8 }}>
                                <Select>
                                    <Option value="desc">Descending</Option>
                                    <Option value="asc">Ascending</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <div style={{ textAlign: 'right', marginTop: 8 }}>
                        <Space>
                            <Button onClick={handleReset}>Reset</Button>
                            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Generate</Button>
                        </Space>
                    </div>
                </Form>
            </Card>

            <Table
                bordered
                size="small"
                columns={columns}
                dataSource={orders}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    showTotal: (total) => `Total ${total} orders`,
                    size: 'default'
                }}
                onChange={handleTableChange}
                scroll={{ x: 800 }}
            />
        </div>
    );
};

export default OrderReports;
